import React, { useCallback, useEffect, useState } from "react";
import {
  Edge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";

import {
  COLLECTION_ID,
  SelectionType,
} from "@highnote/server/src/core/entities";
import { useHighnote } from "App/common/useHighnote";
import { useToast } from "App/common/useToast";
import { sanitizeSelections } from "App/components/useEntitiesSelection";
import { ENTITY_TYPE } from "../EntityTable/config";
import { DragData, HighnoteDropTargetRecord } from "./types";

export type ContainerId = string;

type DnDOperation = ReturnType<typeof determineDnDOperation>;

export type ContainerApi = {
  // Must return a fresh, current ordering of item ids
  getItemsOrder: () => string[];
  // Apply a new ordering (optimistic UI + persistence)
  applyReorder?: (newOrder: string[]) => void;
  // Metadata about what this container represents, used to build target entity
  containerEntityType?: ENTITY_TYPE;
  // Optimistic update (used for top-level spaces)
  optimisticUpdate?: {
    apply: (newOrder: string[]) => void;
    revert: () => void;
    clear?: () => void;
  };
  config?: {
    [key in DnDOperation]?: {
      disableOperation?: boolean;
      disableOperationCallback?: () => void;
      successCallback?: () => void;
    };
  };
};

type DnDMonitorContextType = {
  registerDnDContainer: (containerId: ContainerId, api: ContainerApi) => void;
  unregisterDnDContainer: (containerId: ContainerId) => void;
  updateDnDSelectedEntities: (entities: Record<string, SelectionType>) => void;
  isDnDMonitorLoading: boolean;
  itemsMovingInDnDMonitor: Set<string>;
};

const DnDMonitorContext = React.createContext<DnDMonitorContextType>(null);

export const useDnDMonitor = () => {
  const ctx = React.useContext(DnDMonitorContext);
  if (!ctx) {
    throw new Error("useDnDMonitor must be used within DnDMonitorProvider");
  }
  return ctx;
};

function entityTypeToCollection(
  entityType?: ENTITY_TYPE,
): COLLECTION_ID.TRACK | COLLECTION_ID.SPACE | COLLECTION_ID.FILE {
  switch (entityType) {
    case ENTITY_TYPE.TRACK:
      return COLLECTION_ID.TRACK;
    case ENTITY_TYPE.SPACE:
      return COLLECTION_ID.SPACE;
    default:
      return COLLECTION_ID.FILE;
  }
}

function inferParentContainerCollection(
  containerEntityType?: ENTITY_TYPE,
): COLLECTION_ID.TRACK | COLLECTION_ID.SPACE {
  return containerEntityType === ENTITY_TYPE.TRACK
    ? COLLECTION_ID.TRACK
    : COLLECTION_ID.SPACE;
}

function buildSelectionsForMove({
  sourceId,
  sourceData,
  sourceContainerId,
  srcApi,
  selectedEntities,
}: {
  sourceId: string;
  sourceData: DragData;
  sourceContainerId?: ContainerId;
  srcApi?: ContainerApi;
  selectedEntities: Record<string, SelectionType>;
}): SelectionType[] {
  const entries = Object.keys(selectedEntities);
  if (entries.length > 0) {
    return sanitizeSelections(selectedEntities);
  }
  const entityTypeCollection = entityTypeToCollection(sourceData.type);
  const parentEntity = sourceContainerId
    ? {
        entityId: sourceContainerId,
        entityType: inferParentContainerCollection(srcApi?.containerEntityType),
      }
    : undefined;

  return [
    {
      entityId: sourceId,
      entityType: entityTypeCollection,
      parentEntity,
    },
  ];
}

type DropResolution = {
  sourceData: DragData;
  sourceId: string;
  sourceContainerId?: ContainerId;
  destinationContainerId: ContainerId;
  targetId?: string;
  edge: Edge;
  destApi: ContainerApi;
};

const determineDnDOperation = (
  ctx: DropResolution,
  dropTargets: Array<HighnoteDropTargetRecord>,
) => {
  const { edge, targetId, sourceContainerId, destinationContainerId } = ctx;

  // Center drop combine: no edge + target entity
  if (!edge && targetId) {
    // Validate that the target is a valid entity type for combining
    const targetDropTarget = dropTargets.find((t) => t.data.id === targetId);
    const targetType = targetDropTarget?.data.type;

    if (targetType === ENTITY_TYPE.TRACK || targetType === ENTITY_TYPE.SPACE) {
      return "center-drop-combine";
    }

    // Invalid target type for combining
    return "no-operation";
  }

  // Same level reorder: edge + same container
  if (edge && sourceContainerId === destinationContainerId) {
    return "same-level-reorder";
  }

  // Cross-container combine and reorder: edge + different containers
  if (edge && sourceContainerId !== destinationContainerId) {
    return "combine-and-reorder";
  }

  // Fallback: no edge + no target (shouldn't happen in normal cases)
  return "no-operation";
};

const prepareReorderData = (
  containerApi: ContainerApi,
  selections?: SelectionType[],
) => {
  // Get the current items in the container
  const destItems = containerApi.getItemsOrder();
  // Create a new order array with the current items
  const newOrder = [...(destItems || [])];
  // Get the moved item ids
  const movedItemIds = selections?.map((s) => s.entityId) || [];

  return { destItems, movedItemIds, newOrder };
};

export const DnDMonitorProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { addToastMessage } = useToast();
  // Registry of container APIs
  const containersRef = React.useRef<Map<ContainerId, ContainerApi>>(new Map());
  const { moveSelectionToEntity } = useHighnote();
  const [isDnDMonitorLoading, setIsDnDMonitorLoading] = useState(false);
  // Use a ref to avoid re-rendering the entire component
  const itemsMovingInDnDMonitorRef = React.useRef<Set<string>>(new Set());

  /**
   * Use a ref for selectedEntities to avoid re-rendering the entire component.
   *
   * selectedEntitiesRef: Stores entities selected via the selection UI (checkboxes, etc.)
   * itemsMovingInDnDMonitorRef: Tracks entities currently being moved during drag operations
   *
   * These serve different purposes - selectedEntitiesRef maps to useEntitiesSelection hook
   * and doesn't include single moved items like itemsMovingInDnDMonitorRef does.
   */
  const selectedEntitiesRef = React.useRef<Record<string, SelectionType>>({});
  const updateDnDSelectedEntities = useCallback(
    (entities: Record<string, SelectionType>) => {
      selectedEntitiesRef.current = entities || {};
    },
    [],
  );

  const registerDnDContainer = useCallback(
    (containerId: ContainerId, api: ContainerApi) => {
      containersRef.current.set(containerId, api);
    },
    [],
  );

  const unregisterDnDContainer = useCallback((containerId: ContainerId) => {
    containersRef.current.delete(containerId);
  }, []);

  const resolveDrop = (
    source: { data: DragData },
    dropTargets: Array<HighnoteDropTargetRecord>,
  ): DropResolution | null => {
    if (!dropTargets || dropTargets.length === 0) return null;

    // Atlaskit returns dropTargets from closest → farthest.
    // Index 0 is the nearest element under the pointer (primary destination).
    const destination = dropTargets[0];

    // Guards here are intentionally verbose for containerTarget/itemTarget:
    // - Atlaskit supplies plain objects that may include symbols/undefined/other metadata
    // - We only consider valid string identifiers as targets
    // - For nested containers, prioritize the closest container (index 0)
    const containerTarget = dropTargets.find((t) => {
      return !!t.data && typeof t.data.containerId === "string";
    });
    const itemTarget = dropTargets.find((t) => {
      return !!t.data && typeof t.data.id === "string";
    });

    // For nested containers, use the closest container (destination) if it's registered
    // This ensures we use the nested space instead of the parent space
    const closestContainerId = destination.data.containerId;
    const closestContainerIsRegistered =
      containersRef.current.has(closestContainerId);

    const destinationContainerId = closestContainerIsRegistered
      ? closestContainerId
      : containerTarget?.data.containerId;

    const sourceData = source.data;
    const sourceId = sourceData.id;
    const sourceContainerId = sourceData.containerId;
    const targetId = itemTarget?.data.id || destination.data.id;

    if (!sourceId || !destinationContainerId) {
      // DnDMonitor - missing context
      return null;
    }
    if (sourceId === targetId) {
      // DnDMonitor - ignoring drop on self
      return null;
    }

    const destApi = containersRef.current.get(destinationContainerId);
    if (!destApi) {
      // DnDMonitor - destination container not registered
      return null;
    }

    // Extract edge from the destination data
    // Check if there's a combine instruction first, then fall back to edge detection
    const symbols = Object.getOwnPropertySymbols(destination.data);
    const instructionSymbol = symbols.find((sym) =>
      sym.toString().includes("list-item-instruction"),
    );
    const instruction = instructionSymbol
      ? ((destination.data as Record<symbol, unknown>)[instructionSymbol] as {
          operation?: string;
        })
      : undefined;

    const edge =
      instruction?.operation === "combine"
        ? undefined
        : extractClosestEdge(destination.data);

    return {
      sourceData,
      sourceId,
      sourceContainerId,
      destinationContainerId,
      targetId,
      edge,
      destApi,
    };
  };

  // Build selections including dragged item and any existing selections
  const buildSelectionsForGroupedMove = (
    ctx: DropResolution,
  ): SelectionType[] => {
    const { sourceId, sourceData, sourceContainerId } = ctx;
    const srcApi = sourceContainerId
      ? containersRef.current.get(sourceContainerId)
      : undefined;

    const selectedEntities = selectedEntitiesRef.current;
    const selectionCount = Object.keys(selectedEntities).length;

    if (selectionCount > 0) {
      // Include dragged item in selection if not already selected
      const unionMap = { ...selectedEntities };
      if (!unionMap[sourceId]) {
        const single = buildSelectionsForMove({
          sourceId,
          sourceData,
          sourceContainerId,
          srcApi,
          selectedEntities,
        })[0];
        if (single) {
          unionMap[sourceId] = single;
        }
      }
      return sanitizeSelections(unionMap);
    } else {
      // Just the dragged item
      return buildSelectionsForMove({
        sourceId,
        sourceData,
        sourceContainerId,
        srcApi,
        selectedEntities,
      });
    }
  };

  // Helper function to reorder items within a container
  const reorderItemsInContainer = ({
    containerApi,
    selections,
    insertIndex,
  }: {
    containerApi: ContainerApi;
    selections: SelectionType[];
    insertIndex: number;
  }) => {
    if (!containerApi.applyReorder) return;

    const destItems = containerApi.getItemsOrder();
    if (!destItems) return;

    const newOrder = [...destItems];
    const movedItemIds = selections.map((s) => s.entityId);

    // Remove all moved items from their current positions
    movedItemIds.forEach((movedItemId) => {
      const currentIndex = newOrder.indexOf(movedItemId);
      if (currentIndex !== -1) {
        newOrder.splice(currentIndex, 1);
      }
    });

    // Insert all moved items as a block at the target position
    newOrder.splice(insertIndex, 0, ...movedItemIds);

    // Apply the new order
    containerApi.applyReorder(newOrder);
  };

  const resetDnDMonitor = () => {
    itemsMovingInDnDMonitorRef.current.clear();
    setIsDnDMonitorLoading(false);
  };

  useEffect(() => {
    const cleanup = monitorForElements({
      onDrop: async ({ source, location }) => {
        const dropTargets = location.current
          .dropTargets as unknown as Array<HighnoteDropTargetRecord>;
        const ctx = resolveDrop(
          source as unknown as { data: DragData },
          dropTargets,
        );

        if (!ctx) return;

        const {
          destinationContainerId,
          edge,
          targetId,
          sourceContainerId,
          sourceId,
        } = ctx;

        // Determine final destination
        const finalDestApi = ctx.destApi;

        // Determine the type of DnD operation
        const operation = determineDnDOperation(ctx, dropTargets);

        switch (operation) {
          case "center-drop-combine": {
            const targetDropTarget = dropTargets.find(
              (t) => t.data.id === targetId,
            );
            const targetType = targetDropTarget?.data.type;

            const selections = buildSelectionsForGroupedMove(ctx);
            if (selections.length > 0) {
              setIsDnDMonitorLoading(true);
              selections.forEach((s) => {
                itemsMovingInDnDMonitorRef.current.add(s.entityId);
              });

              // For center-drop-combine, we're moving items INTO a container
              // So we need to add the moved items to the existing items in the destination
              const { movedItemIds, newOrder } = prepareReorderData(
                finalDestApi,
                selections,
              );

              // Remove moved items from the top-level list
              movedItemIds.forEach((movedId) => {
                const index = newOrder.indexOf(movedId);
                if (index !== -1) {
                  newOrder.splice(index, 1);
                }
              });

              if (finalDestApi.optimisticUpdate) {
                finalDestApi.optimisticUpdate.apply(newOrder);
              }

              itemsMovingInDnDMonitorRef.current.add(targetId);

              try {
                await moveSelectionToEntity({
                  targetEntity: {
                    entityId: targetId,
                    entityType: inferParentContainerCollection(targetType),
                  },
                  selectedEntities: selections,
                });
              } catch (error) {
                if (finalDestApi.optimisticUpdate) {
                  finalDestApi.optimisticUpdate.revert();
                }

                addToastMessage(
                  "error",
                  "There was an error combining the items. Please try again.",
                  {
                    title: "Error Combining Items",
                  },
                );
              } finally {
                finalDestApi.optimisticUpdate?.clear?.();
                resetDnDMonitor();
              }
            }
            break;
          }

          case "same-level-reorder": {
            const config = finalDestApi.config?.["same-level-reorder"];
            if (config?.disableOperation) {
              config?.disableOperationCallback?.();
              break;
            }

            // Compute insert index for reordering
            const destItems = finalDestApi.getItemsOrder();
            if (!destItems) return;

            setIsDnDMonitorLoading(true);

            // Use the helper function to reorder items
            const selections = buildSelectionsForGroupedMove(ctx);
            selections.forEach((s) => {
              itemsMovingInDnDMonitorRef.current.add(s.entityId);
            });

            let insertIndex = destItems.length;
            if (targetId) {
              const baseIndex = destItems.indexOf(targetId);

              // Get the instruction from the drop target to determine the operation
              const targetDropTarget = dropTargets.find(
                (t) => t.data.id === targetId,
              );
              const symbols = Object.getOwnPropertySymbols(
                targetDropTarget?.data || {},
              );
              const instructionSymbol = symbols.find((sym) =>
                sym.toString().includes("list-item-instruction"),
              );
              const instruction = instructionSymbol
                ? ((targetDropTarget?.data as Record<symbol, unknown>)[
                    instructionSymbol
                  ] as {
                    operation?: string;
                  })
                : undefined;

              // Determine insert position based on edge or instruction
              const shouldInsertAfter =
                instruction?.operation === "reorder-after" || edge === "bottom";
              insertIndex = baseIndex + (shouldInsertAfter ? 1 : 0);

              // Fix: Adjust insert index when moving within the same container
              // If the dragged item is in the same container and comes before the insert position,
              // we need to subtract 1 from the insert index because the item will be removed first
              if (sourceContainerId === destinationContainerId) {
                const sourceIndex = destItems.indexOf(sourceId);
                if (sourceIndex !== -1 && sourceIndex < insertIndex) {
                  insertIndex -= 1;
                }
              }
            }

            reorderItemsInContainer({
              containerApi: finalDestApi,
              selections,
              insertIndex,
            });

            resetDnDMonitor();
            break;
          }

          case "combine-and-reorder": {
            const selections = buildSelectionsForGroupedMove(ctx);
            if (selections.length > 0) {
              const { destItems, movedItemIds, newOrder } = prepareReorderData(
                finalDestApi,
                selections,
              );
              selections.forEach((s) => {
                itemsMovingInDnDMonitorRef.current.add(s.entityId);
              });

              if (!destItems) return;

              setIsDnDMonitorLoading(true);

              // Add moved items to the destination container
              newOrder.push(...movedItemIds);

              if (finalDestApi.optimisticUpdate) {
                finalDestApi.optimisticUpdate?.apply(newOrder);
              }

              try {
                // For cross-container moves, don't immediately remove from UI
                // Let the server handle the move and update the UI through the response
                await moveSelectionToEntity({
                  targetEntity:
                    // Map library-home to null for top-level moves (matches row actions behavior)
                    destinationContainerId === "library-home"
                      ? null
                      : {
                          entityId: destinationContainerId,
                          entityType: inferParentContainerCollection(
                            finalDestApi.containerEntityType,
                          ),
                        },
                  selectedEntities: selections,
                });

                // After the move, reorder based on the edge data we already have
                if (
                  // TODO: There is a race condition for tracks only when reordering items within the track.
                  // It causes the item to be removed from the track entirely due to us updating with outdated data via allTrackChildEntityRows.
                  // We should find a better way to handle this. Removed applyReorder check for tracks in registerDnDContainer.
                  // https://www.notion.so/highnotefm/Update-drag-and-reorder-process-for-Tracks-Race-condition-25b15dd14828800bb816d622fdf01dd7
                  finalDestApi.containerEntityType !== ENTITY_TYPE.TRACK &&
                  finalDestApi.applyReorder &&
                  targetId &&
                  edge
                ) {
                  // Get the latest items in the container as they may be stale from the recent moveSelectionToEntity call
                  const latestDestItems = finalDestApi.getItemsOrder();

                  if (latestDestItems) {
                    const baseIndex = latestDestItems.indexOf(targetId);
                    if (baseIndex !== -1) {
                      // Use the edge data to determine insert position
                      const shouldInsertAfter = edge === "bottom";
                      const insertIndex =
                        baseIndex + (shouldInsertAfter ? 1 : 0);

                      // Use the helper function to reorder items
                      reorderItemsInContainer({
                        containerApi: finalDestApi,
                        selections,
                        insertIndex,
                      });
                    }
                  }
                }

                // Call the success callback if it exists
                finalDestApi.config["combine-and-reorder"]?.successCallback?.();
              } catch (error) {
                finalDestApi.optimisticUpdate?.revert();
              } finally {
                finalDestApi.optimisticUpdate?.clear?.();
                resetDnDMonitor();
              }
            }
            break;
          }

          default: {
            break;
          }
        }
      },
    });

    return cleanup;
  }, []);

  const value: DnDMonitorContextType = {
    registerDnDContainer,
    unregisterDnDContainer,
    updateDnDSelectedEntities,
    isDnDMonitorLoading,
    itemsMovingInDnDMonitor: itemsMovingInDnDMonitorRef.current,
  };

  return (
    <DnDMonitorContext.Provider value={value}>
      {children}
    </DnDMonitorContext.Provider>
  );
};
